/**
 * Utility functions for asset optimization and performance
 */

// Image optimization utilities
export const imageOptimization = {
  /**
   * Generate responsive image URLs for different screen sizes
   */
  generateResponsiveUrls: (baseUrl: string, sizes: number[] = [320, 640, 1024, 1920]) => {
    return sizes.map(size => ({
      size,
      url: `${baseUrl}?w=${size}&q=80&f=webp`,
      media: `(max-width: ${size}px)`,
    }));
  },

  /**
   * Generate srcSet string for responsive images
   */
  generateSrcSet: (baseUrl: string, sizes: number[] = [320, 640, 1024, 1920]) => {
    return sizes
      .map(size => `${baseUrl}?w=${size}&q=80&f=webp ${size}w`)
      .join(', ');
  },

  /**
   * Get optimized image URL with quality and format parameters
   */
  getOptimizedUrl: (
    url: string, 
    options: {
      width?: number;
      height?: number;
      quality?: number;
      format?: 'webp' | 'jpeg' | 'png';
      fit?: 'cover' | 'contain' | 'fill';
    } = {}
  ) => {
    const { width, height, quality = 80, format = 'webp', fit = 'cover' } = options;
    const params = new URLSearchParams();
    
    if (width) params.set('w', width.toString());
    if (height) params.set('h', height.toString());
    params.set('q', quality.toString());
    params.set('f', format);
    params.set('fit', fit);
    
    return `${url}?${params.toString()}`;
  },

  /**
   * Check if WebP is supported by the browser
   */
  isWebPSupported: (): Promise<boolean> => {
    return new Promise((resolve) => {
      const webP = new Image();
      webP.onload = webP.onerror = () => {
        resolve(webP.height === 2);
      };
      webP.src = 'data:image/webp;base64,UklGRjoAAABXRUJQVlA4IC4AAACyAgCdASoCAAIALmk0mk0iIiIiIgBoSygABc6WWgAA/veff/0PP8bA//LwYAAA';
    });
  },

  /**
   * Get the best supported image format
   */
  getBestFormat: async (): Promise<'webp' | 'jpeg'> => {
    const supportsWebP = await imageOptimization.isWebPSupported();
    return supportsWebP ? 'webp' : 'jpeg';
  },
};

// Resource preloading utilities
export const resourcePreloading = {
  /**
   * Preload critical images
   */
  preloadImages: (urls: string[]) => {
    urls.forEach(url => {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = url;
      document.head.appendChild(link);
    });
  },

  /**
   * Preload critical CSS
   */
  preloadCSS: (href: string) => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'style';
    link.href = href;
    link.onload = () => {
      link.rel = 'stylesheet';
    };
    document.head.appendChild(link);
  },

  /**
   * Preload JavaScript modules
   */
  preloadScript: (src: string) => {
    const link = document.createElement('link');
    link.rel = 'modulepreload';
    link.href = src;
    document.head.appendChild(link);
  },

  /**
   * Prefetch resources for future navigation
   */
  prefetchResource: (href: string, as?: string) => {
    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = href;
    if (as) link.as = as;
    document.head.appendChild(link);
  },
};

// Performance monitoring utilities
export const performanceMonitoring = {
  /**
   * Measure and log Core Web Vitals
   */
  measureCoreWebVitals: () => {
    // Largest Contentful Paint (LCP)
    new PerformanceObserver((entryList) => {
      const entries = entryList.getEntries();
      const lastEntry = entries[entries.length - 1];
      console.log('LCP:', lastEntry.startTime);
    }).observe({ entryTypes: ['largest-contentful-paint'] });

    // First Input Delay (FID)
    new PerformanceObserver((entryList) => {
      const entries = entryList.getEntries();
      entries.forEach((entry) => {
        console.log('FID:', (entry as any).processingStart - entry.startTime);
      });
    }).observe({ entryTypes: ['first-input'] });

    // Cumulative Layout Shift (CLS)
    let clsValue = 0;
    new PerformanceObserver((entryList) => {
      const entries = entryList.getEntries();
      entries.forEach((entry: any) => {
        if (!entry.hadRecentInput) {
          clsValue += entry.value;
        }
      });
      console.log('CLS:', clsValue);
    }).observe({ entryTypes: ['layout-shift'] });
  },

  /**
   * Measure resource loading times
   */
  measureResourceTiming: (resourceType?: string) => {
    const resources = performance.getEntriesByType('resource');
    const filtered = resourceType 
      ? resources.filter(resource => resource.name.includes(resourceType))
      : resources;
    
    return filtered.map(resource => ({
      name: resource.name,
      duration: resource.duration,
      size: (resource as any).transferSize || 0,
      type: (resource as any).initiatorType,
    }));
  },

  /**
   * Get navigation timing metrics
   */
  getNavigationTiming: () => {
    const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
    
    return {
      dns: navigation.domainLookupEnd - navigation.domainLookupStart,
      tcp: navigation.connectEnd - navigation.connectStart,
      request: navigation.responseStart - navigation.requestStart,
      response: navigation.responseEnd - navigation.responseStart,
      dom: navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart,
      load: navigation.loadEventEnd - navigation.loadEventStart,
      total: navigation.loadEventEnd - (navigation as any).navigationStart,
    };
  },
};

// Bundle optimization utilities
export const bundleOptimization = {
  /**
   * Dynamically import modules with error handling
   */
  dynamicImport: async <T>(importFn: () => Promise<T>): Promise<T | null> => {
    try {
      return await importFn();
    } catch (error) {
      console.error('Dynamic import failed:', error);
      return null;
    }
  },

  /**
   * Check if a feature should be loaded based on user preferences
   */
  shouldLoadFeature: (featureName: string): boolean => {
    // Check user preferences, device capabilities, etc.
    const userPrefs = JSON.parse(localStorage.getItem('userPreferences') || '{}');
    const isLowEndDevice = navigator.hardwareConcurrency <= 2;
    const isSlowConnection = (navigator as any).connection?.effectiveType === 'slow-2g';
    
    // Don't load heavy features on low-end devices or slow connections
    if (isLowEndDevice || isSlowConnection) {
      const heavyFeatures = ['charts', 'animations', 'video'];
      return !heavyFeatures.includes(featureName);
    }
    
    return userPrefs[featureName] !== false;
  },

  /**
   * Get optimal chunk loading strategy
   */
  getChunkLoadingStrategy: () => {
    const connection = (navigator as any).connection;
    const isSlowConnection = connection?.effectiveType === 'slow-2g' || connection?.effectiveType === '2g';
    const isLimitedData = connection?.saveData;
    
    if (isSlowConnection || isLimitedData) {
      return 'minimal'; // Load only essential chunks
    }
    
    if (connection?.effectiveType === '4g') {
      return 'aggressive'; // Preload more chunks
    }
    
    return 'balanced'; // Default strategy
  },
};

// Memory optimization utilities
export const memoryOptimization = {
  /**
   * Clean up unused resources
   */
  cleanup: () => {
    // Clear unused images from memory
    const images = document.querySelectorAll('img[data-cleanup="true"]');
    images.forEach(img => {
      if (!(img as HTMLImageElement).complete) {
        (img as HTMLImageElement).src = '';
      }
    });
    
    // Suggest garbage collection (if available)
    if ('gc' in window) {
      (window as any).gc();
    }
  },

  /**
   * Monitor memory usage
   */
  getMemoryInfo: () => {
    if ('memory' in performance) {
      const memory = (performance as any).memory;
      return {
        used: memory.usedJSHeapSize,
        total: memory.totalJSHeapSize,
        limit: memory.jsHeapSizeLimit,
        usage: (memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100,
      };
    }
    return null;
  },

  /**
   * Check if memory usage is high
   */
  isMemoryUsageHigh: (): boolean => {
    const memInfo = memoryOptimization.getMemoryInfo();
    return memInfo ? memInfo.usage > 80 : false;
  },
};

export default {
  imageOptimization,
  resourcePreloading,
  performanceMonitoring,
  bundleOptimization,
  memoryOptimization,
};